import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/free-solid-svg-icons";

/**
 * DS Pagination — 좌우 chevron + N개 페이지 cell.
 * Figma 시안에서는 최대 7개 cell 표시. 그 이상은 windowSize prop으로 조정.
 *
 * ellipsis=true (figma 3934:68487 / 4351:23431 "1 2 3 4 5 … 999"):
 *   총 페이지가 windowSize 를 넘으면 창 안에 "…" 와 첫/마지막 페이지를 넣어 항상 windowSize 개 셀을 유지한다.
 *   기본 false = 슬라이딩 창 동작.
 *
 * @param {object} props
 * @param {number} props.page - 현재 페이지 (1-base)
 * @param {number} props.totalPages
 * @param {(page: number) => void} props.onChange
 * @param {number} [props.windowSize=7]
 * @param {boolean} [props.ellipsis=false]
 * @param {string} [props.className]
 */
const ELLIPSIS = "…";

// ellipsis 모드 셀 배열 — 숫자 또는 ELLIPSIS. windowSize 는 5 이상, 홀수일 때 정확히 windowSize 개.
const buildEllipsisCells = (page, totalPages, windowSize) => {
  const inner = windowSize - 2; // 첫/끝 페이지를 제외한 나머지 칸 수
  if (page <= inner - 1) {
    // 앞쪽: 1..(windowSize-2) … last
    const cells = [];
    for (let i = 1; i <= windowSize - 2; i++) cells.push(i);
    return [...cells, ELLIPSIS, totalPages];
  }
  if (page >= totalPages - (inner - 2)) {
    // 뒤쪽: 1 … (last-(windowSize-3))..last
    const cells = [];
    for (let i = totalPages - (windowSize - 3); i <= totalPages; i++) cells.push(i);
    return [1, ELLIPSIS, ...cells];
  }
  // 가운데: 1 … (page-h..page+h) … last
  const half = Math.floor((windowSize - 4) / 2);
  const cells = [];
  for (let i = page - half; i <= page + half; i++) cells.push(i);
  return [1, ELLIPSIS, ...cells, ELLIPSIS, totalPages];
};

const DSPagination = ({
  page,
  totalPages,
  onChange,
  windowSize = 7,
  ellipsis = false,
  className = "",
}) => {
  if (!totalPages || totalPages < 1) return null;

  let cells = [];
  if (ellipsis && windowSize >= 5 && totalPages > windowSize) {
    cells = buildEllipsisCells(page, totalPages, windowSize);
  } else {
    // window calculation
    const half = Math.floor(windowSize / 2);
    let start = Math.max(1, page - half);
    let end = Math.min(totalPages, start + windowSize - 1);
    start = Math.max(1, end - windowSize + 1);
    for (let i = start; i <= end; i++) cells.push(i);
  }

  const handleClick = (target) => {
    if (target < 1 || target > totalPages || target === page) return;
    onChange && onChange(target);
  };

  return (
    <div className={`ds-pagination ${className}`}>
      <button
        type="button"
        className={`ds-pagination__chevron ${
          page <= 1 ? "ds-pagination__cell--disabled" : ""
        }`}
        onClick={() => handleClick(page - 1)}
        aria-label="이전"
      >
        <FontAwesomeIcon icon={faAngleLeft} />
      </button>
      {cells.map((n, idx) =>
        n === ELLIPSIS ? (
          <span key={`ellipsis-${idx}`} className="ds-pagination__ellipsis" aria-hidden>
            ...
          </span>
        ) : (
          <button
            key={n}
            type="button"
            className={`ds-pagination__cell ${
              n === page ? "ds-pagination__cell--active" : ""
            }`}
            onClick={() => handleClick(n)}
            aria-current={n === page ? "page" : undefined}
          >
            {n}
          </button>
        ),
      )}
      <button
        type="button"
        className={`ds-pagination__chevron ${
          page >= totalPages ? "ds-pagination__cell--disabled" : ""
        }`}
        onClick={() => handleClick(page + 1)}
        aria-label="다음"
      >
        <FontAwesomeIcon icon={faAngleRight} />
      </button>
    </div>
  );
};

export default React.memo(DSPagination);
